﻿/**
 * dominoAssets.ts — Centralized domino tile asset resolver.
 *
 * Single source of truth for:
 *   - Phaser texture cache keys  (used in DominoTile, ShuffleDealAnimator, preload)
 *   - Public asset paths         (used in GameScene.preload)
 *
 * Asset location: /Assets/game/tiles/tiles_328x164/
 * Naming convention: domino_${lo}-${hi}.png  (low-high canonical order)
 * Back tile: domino_back.png
 *
 * All display sizing is handled by setDisplaySize() at the call site, so raw
 * PNG dimensions NEVER affect layout — only these keys and paths matter.
 *
 * PNG spec: 328×164 — the universal tile set (board, hands, backs, overlays),
 * sized at ~2x the largest on-screen use (spec hand tiles, 164×82) so WebGL's
 * non-mipmapped LINEAR minification stays in its sharp range everywhere.
 * Replaces the former 512×256 set (which downscaled ~5x on the board and
 * went soft) and the temporary 84×42 native-hand experiment.
 */

/** Phaser texture cache key for a face-up domino tile. Always canonical (lo ≤ hi). */
export function getDominoTextureKey(a: number, b: number): string {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  return `domino-${lo}-${hi}`;
}

/** Phaser texture cache key for the face-down (back) tile. */
export function getDominoBackTextureKey(): string {
  return 'domino-back';
}

/** Public asset path for a face-up tile. Used in preload only. */
export function getDominoAssetPath(a: number, b: number): string {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  return `/Assets/game/tiles/tiles_328x164/domino_${lo}-${hi}.png`;
}

/** Public asset path for the face-down (back) tile. Used in preload only. */
export function getDominoBackAssetPath(): string {
  return '/Assets/game/tiles/tiles_328x164/domino_back.png';
}

// ── TEMPORARY — native 84×42 hand-tile quality experiment ─────────────────
// See src/config/handTileExperiment.ts (USE_NATIVE_84X42_HAND_TILES). These
// resolve to a separate, pre-generated asset set and a separate texture-cache
// key namespace ("domino-native-…") so they never collide with or affect the
// production keys/paths above. Face-only — the local hand never shows a
// face-down tile, so no native "back" variant exists or is needed.

/** Texture cache key for the native-resolution (84×42) hand-tile experiment. */
export function getDominoNativeHandTextureKey(a: number, b: number): string {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  return `domino-native-${lo}-${hi}`;
}

/** Public asset path for the native-resolution (84×42) hand-tile experiment. */
export function getDominoNativeHandAssetPath(a: number, b: number): string {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  return `/Assets/game/dominoes-hand-84x42/domino_${lo}-${hi}.png`;
}
